import type { XbotCapabilities, XbotSuggestSelected, XbotUtf16Range } from "@dto/pyxbot-agent.dto";
import type { KeyboardEvent } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Log } from "src/service/log.service";
import { sendXbotSuggest } from "./client";
import { acceptSuggestion, bindingBefore, bindingOver, caretOf, Draft, fragmentAt, FragmentAt } from "./draft";
import {
  answered, askNow, AskContext, buttonState, closeOffer, dismiss, due, FlowStep, forDraft, keyIntent, moveActive,
  Offer, offersSearchAll, quietText, startFlow, SuggestFlow, typed,
} from "./flow";
import { optionId, searchAllId } from "./SuggestionList";

export interface SuggestionsOptions {
  draftId: string;
  /** null until read; nothing is offered unless the service supports both flags. */
  capabilities: XbotCapabilities | null;
  /** creates a thread on the fly if the conversation has none yet */
  getThreadId: () => string | null;
  getInput: () => unknown;
  getDraft: () => Draft;
  /** caret lands right after the inserted text */
  onPicked: (draft: Draft, caret: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onThreadAsked: (threadId: string) => void;
  onUnsupported: () => void;
}

export interface Suggestions {
  offer: Offer | null;
  active: number;
  listId: string;
  searchAll: boolean;
  quiet: string | null;
  button: { disabled: boolean; reason: string };
  onChange: (draft: Draft) => void;
  /** false stops Enter from sending, since it picked instead */
  onKeyDown: (event: KeyboardEvent) => void | false;
  accept: (index: number) => void;
  close: () => void;
  manual: () => void;
  searchAllOfNg: () => void;
  pickAgain: (bindingIndex: number) => void;
  /** The Objects button's search while it is open (ObjectSearch.tsx); null while it is shut. */
  search: ObjectSearchOpen | null;
  closeSearch: () => void;
}

/** What the Objects button's search was opened on. */
export interface ObjectSearchOpen {
  /** The word at the cursor, which a pick replaces; null when there was none (the pick is put at the cursor). */
  word: FragmentAt | null;
  /** Where the pick goes: the word's start, or the cursor. */
  start: number;
  selected: XbotSuggestSelected | null;
  /** New for every opening, so the search starts afresh. */
  key: number;
}

/** The object picked nearest before the words: objects of its platform and kind come first in the offer. */
function selectedFor(draft: Draft, range: XbotUtf16Range): XbotSuggestSelected | null {
  const before = bindingBefore(draft, range);
  return before ? { item_id: before.target.item_id, platform: before.target.platform, kind: before.target.kind } : null;
}

function isComposing(event: KeyboardEvent): boolean {
  const native = event.nativeEvent as unknown as { isComposing?: boolean } | undefined;
  return Boolean(native?.isComposing) || event.key === "Process";
}

/** Runs the offer rules from flow.ts/draft.ts: debounce, in-flight request, and a11y wiring to the text box. */
export function useSuggestions(options: SuggestionsOptions): Suggestions {
  const [flow, setFlowState] = useState<SuggestFlow>(() => startFlow(options.draftId));
  const flowRef = useRef(flow);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const listId = `xm-chat-suggest-list${useId()}`;
  const [search, setSearch] = useState<ObjectSearchOpen | null>(null);
  const enabled = Boolean(options.capabilities?.bindings && options.capabilities?.suggest);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const runner = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let aborter: AbortController | null = null;

    const setFlow = (next: SuggestFlow) => {
      flowRef.current = next;
      setFlowState(next);
    };

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const contextFor = (range: XbotUtf16Range): AskContext => ({
      threadId: optionsRef.current.getThreadId(),
      selected: selectedFor(optionsRef.current.getDraft(), range),
    });

    const wordAt = (text: string): FragmentAt | null => {
      const caret = caretOf(optionsRef.current.getInput());
      return caret ? fragmentAt(text, caret.start, caret.end) : null;
    };

    const run = (result: FlowStep) => {
      // An answer for another draft, or one that changes nothing, leaves the typing wait alone.
      if (result.flow === flowRef.current && !result.send && result.wait === null && !result.abort && !result.unsupported) {
        return;
      }
      if (result.abort && aborter) {
        aborter.abort();
        aborter = null;
      }
      setFlow(result.flow);
      if (result.unsupported) {
        optionsRef.current.onUnsupported();
      }
      if (result.send) {
        send(result.send);
      }
      if (result.wait !== null) {
        clearTimer();
        timer = setTimeout(() => {
          timer = null;
          const current = flowRef.current;
          if (current.want) {
            run(due(current, contextFor(current.want.range)));
          }
        }, result.wait);
      } else if (!result.flow.want) {
        clearTimer();
      }
    };

    const send = (body: Parameters<typeof sendXbotSuggest>[0]) => {
      const own = new AbortController();
      aborter = own;
      optionsRef.current.onThreadAsked(body.thread_id);
      void sendXbotSuggest(body, own.signal).then((outcome) => {
        if (aborter === own) {
          aborter = null;
        }
        if (outcome.kind === "refused" && outcome.status >= 400 && outcome.status < 500 && outcome.status !== 429) {
          // Not for the person: the offer only does not open, the question can still be asked, and a refusal here (a
          // malformed request, a pick no longer in the conversation, no rights, the demo) is nothing they can act on
          // while typing. Kept quietly for whoever debugs it. A login that has ended is the exception: xbotFetch signs
          // out on it, as every other request of XorMon does.
          Log.debug(`Xbot suggestions refused (${outcome.status} ${outcome.error.code}): ${outcome.error.message}`);
        }
        // The cursor may have left the word without the text changing (a click, an arrow key); the answer checks.
        const text = optionsRef.current.getDraft().text;
        run(answered(flowRef.current, body, outcome, text, wordAt(text)));
      });
    };

    const stop = () => {
      clearTimer();
      aborter?.abort();
      aborter = null;
    };

    return { run, setFlow, clearTimer, contextFor, wordAt, stop };
  }, []);

  // A new draft starts the numbering again and drops whatever is still out for the old one.
  useEffect(() => {
    if (flowRef.current.draftId !== options.draftId) {
      runner.run(forDraft(flowRef.current, options.draftId));
    }
  }, [options.draftId, runner]);

  // The service stopped taking picks (a 501 mid-session): what is open shuts, and what is out is dropped.
  useEffect(() => {
    const current = flowRef.current;
    if (!enabled && (current.offer || current.want || current.inFlight)) {
      runner.run(closeOffer(current));
    }
  }, [enabled, runner]);

  useEffect(() => () => runner.stop(), [runner]);

  const offer = flow.offer;
  const searchAll = offersSearchAll(flow);
  const activeId = flow.active < 0 || !offer
    ? null
    : flow.active < offer.items.length ? optionId(listId, flow.active) : searchAllId(listId);

  useEffect(() => {
    const input = options.getInput();
    if (!input || typeof (input as Element).setAttribute !== "function") {
      return;
    }
    const box = input as Element;
    if (!enabled) {
      for (const name of ["aria-autocomplete", "aria-controls", "aria-expanded", "aria-activedescendant"]) {
        box.removeAttribute(name);
      }
      return;
    }
    box.setAttribute("aria-autocomplete", "list");
    box.setAttribute("aria-controls", listId);
    box.setAttribute("aria-expanded", offer ? "true" : "false");
    if (activeId) {
      box.setAttribute("aria-activedescendant", activeId);
    } else {
      box.removeAttribute("aria-activedescendant");
    }
  });

  const wordAtCursor = (draft: Draft): FragmentAt | null => runner.wordAt(draft.text);

  const onChange = (draft: Draft) => {
    if (!enabledRef.current) {
      return;
    }
    const at = wordAtCursor(draft);
    // A name already picked is not offered again while typing; the button still can.
    runner.run(typed(flowRef.current, at && !bindingOver(draft, at) ? at : null));
  };

  const accept = (index: number) => {
    const current = flowRef.current;
    const shown = current.offer;
    const item = shown?.items[index];
    if (!shown || !item) {
      return;
    }
    const draft = optionsRef.current.getDraft();
    let range: XbotUtf16Range = shown.range;
    let fragment = shown.fragment;
    if (draft.text.slice(range.start, range.end) !== fragment) {
      // The same word grew or shrank since the offer came; the pick takes the word as it is now.
      const at = wordAtCursor(draft);
      if (at?.start !== shown.range.start) {
        runner.run(closeOffer(current));
        return;
      }
      range = { start: at.start, end: at.end };
      fragment = at.fragment;
    }
    const next = acceptSuggestion(draft, range, fragment, item);
    runner.run(closeOffer(current));
    if (next) {
      optionsRef.current.onPicked(next, range.start + item.insert_text.length);
    }
  };

  const askFor = (at: FragmentAt | null, scope?: "all") => {
    if (!enabledRef.current || !at) {
      return;
    }
    // The keys that walk the offer are the text box's; a press elsewhere hands the focus back to it.
    const input = optionsRef.current.getInput() as { focus?: () => void } | null;
    input?.focus?.();
    runner.run(askNow(flowRef.current, at, runner.contextFor({ start: at.start, end: at.end }), scope));
  };

  // The Objects button opens the search with a field of its own, the word at the cursor in it (if any): an object can
  // be looked for by name whatever the cursor stands on. The offer while typing shuts meanwhile.
  const manual = () => {
    if (!enabledRef.current) {
      return;
    }
    const draft = optionsRef.current.getDraft();
    const word = wordAtCursor(draft);
    const caret = caretOf(optionsRef.current.getInput());
    const start = word?.start ?? caret?.end ?? draft.text.length;
    runner.clearTimer();
    runner.run(closeOffer(flowRef.current));
    setSearch((was) => ({ word, start, selected: selectedFor(draft, { start, end: start }), key: (was?.key ?? 0) + 1 }));
  };

  const searchAllOfNg = () => {
    const draft = optionsRef.current.getDraft();
    const shown = flowRef.current.offer;
    const stillThere = Boolean(shown) && draft.text.slice(shown.range.start, shown.range.end) === shown.fragment;
    askFor(stillThere ? { start: shown.range.start, end: shown.range.end, fragment: shown.fragment } : wordAtCursor(draft), "all");
  };

  const pickAgain = (bindingIndex: number) => {
    const binding = optionsRef.current.getDraft().bindings[bindingIndex];
    if (binding) {
      askFor({ start: binding.range.start, end: binding.range.end, fragment: binding.text });
    }
  };

  const onKeyDown = (event: KeyboardEvent): void | false => {
    const intent = keyIntent({
      key: event.key,
      code: event.code,
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      altKey: event.altKey,
      isComposing: isComposing(event),
    }, flowRef.current);
    switch (intent) {
      case "undo":
      case "redo":
        event.preventDefault();
        runner.run(closeOffer(flowRef.current));
        if (intent === "undo") {
          optionsRef.current.onUndo();
        } else {
          optionsRef.current.onRedo();
        }
        return;
      case "next":
      case "previous":
        event.preventDefault();
        runner.setFlow(moveActive(flowRef.current, intent === "next" ? 1 : -1));
        return;
      case "close":
        // Escape shuts the offer only; the chat window, which also closes on Escape, stays open.
        event.preventDefault();
        event.stopPropagation();
        runner.clearTimer();
        runner.setFlow(dismiss(flowRef.current));
        return;
      case "accept":
        event.preventDefault();
        accept(flowRef.current.active);
        return false;
      case "searchAll":
        event.preventDefault();
        searchAllOfNg();
        return false;
      case "search":
        // Ctrl+Space (⌥Space on a Mac): the Objects search, as the button opens it
        event.preventDefault();
        manual();
        return false;
      default:
        return;
    }
  };

  const input = options.getInput();
  const caretReadable = input === null || input === undefined || caretOf(input) !== null;

  return {
    offer,
    active: flow.active,
    listId,
    searchAll,
    quiet: quietText(flow),
    button: buttonState(options.capabilities, flow, caretReadable),
    onChange,
    onKeyDown,
    accept,
    close: () => runner.run(closeOffer(flowRef.current)),
    manual,
    searchAllOfNg,
    pickAgain,
    search,
    closeSearch: () => setSearch(null),
  };
}
